import React, { useState } from "react";
import { Dialog } from "../../../../../../components/Dialogs/Dialog";
import { Grid, SelectChangeEvent, Typography } from "@mui/material";
import { FileUpload } from "../../../../../../features/FileUpload/components/FileUpload";
import {
  useDispatchWrapper,
  usePoliciesByCustomer,
} from "../../../../../../hooks";
import { upsertARSQuote } from "../../../../../../redux/automobileApplication/automobileApplicationActions";
import { getUploadedPdfProccess } from "../../../../../../redux/aiReconciliation/aiReconciliationActions";
import { IUploadPdfDocumentProcess } from "../../../../../../redux/aiReconciliation/aiReconciliation.types";
import {
  ERROR_MESSAGES,
} from "../../../../../../constants/enums";
import { ProgressCard } from "../../../../../../components/ProgressCard/ProgressCard";
import {
  ControlledCustomSelect,
  CustomFormLabel,
} from "../../../../../../components/formsComponents";
import { ICustomerFullDetails } from "../../../../../../redux/customer/customer.types";
import { CustomerAutoSearch } from "../../../../../../components/AutoCompleteSearches/CustomerAutoSearch";
import { CustomAlert } from "../../../../../../components/formsComponents/CustomAlert";

export interface IARSQuoteProps {
  open: boolean;
  customerId?: number;
  customerPolicyId?: number;
  type?: string;
  onComplete: () => void;
  onClose: () => void;
  showCustomerPolicy?: boolean;
}

export const ARSQuoteDialog: React.FC<IARSQuoteProps> = (props) => {
  const {
    open,
    onComplete,
    customerId,
    customerPolicyId,
    onClose,
    type,
    showCustomerPolicy = false,
  } = props;

  const [file, setFile] = React.useState<File | null>(null);
  const dispatch = useDispatchWrapper();
  const [code, setCode] = React.useState<string | null>(null);
  const [subType, setSubType] = React.useState<string>("ONTARIO");
  const [state, setState] = React.useState<IUploadPdfDocumentProcess | null>(
    null
  );
  const [error, setError] = React.useState<string | null>(null);
  const [customerState, setCustomerState] = useState({
    customerId: customerId,
    customerPolicyId: customerPolicyId,
    type: type || "",
  });
  const {
    policiesList,
    policiesLoading,
    fetchPoliciesByCustomerId,
    fetchPolicyOtherInfoByPolicyId,
  } = usePoliciesByCustomer();

  const handleClick = () => {
    if (!customerState.customerId || !customerState.customerPolicyId) {
      setError("Please select customer and policy");
      return;
    }
    if (file) {
      dispatch(
        upsertARSQuote(
          file,
          customerState.customerPolicyId,
          customerState.customerId,
          customerState.type,
          (isSuccess, code, isAlready) => {
            if (isSuccess) {
              if (code) {
                setCode(code);
              } else if (isAlready) {
                onComplete();
              }
            }
          },
          subType
        )
      );
    }
  };

  const handleCustomer = (data: ICustomerFullDetails) => {
    if (data.customer_id) {
      setCustomerState({
        ...customerState,
        customerId: data.customer_id as number,
        customerPolicyId: undefined,
        type: "",
      });
    }
  };

  const handlePolicyChange = (e: SelectChangeEvent<unknown>) => {
    const policyId = e.target.value as string;
    const policy = policiesList.find(
      (x) => x.customer_policy_id === Number(policyId)
    );
    if (policy) {
      setCustomerState({
        ...customerState,
        customerPolicyId: policy.customer_policy_id,
        type:
          policy.line_of_business === "Automobile Policy"
            ? "automobile"
            : policy.line_of_business === "Habitational Policy"
            ? "habitational"
            : policy.line_of_business === "Commercial Auto Policy"
            ? "commercial_auto"
            : policy.line_of_business === "Business Policy"
            ? "business"
            : "",
      });
    }
  };

  const fetchData = (proccessCode: string, interval?: any) => {
    dispatch(
      getUploadedPdfProccess(proccessCode, (isSuccess, data) => {
        if (isSuccess && data) {
          if (data.status === "COMPLETED") {
            onComplete();
            setState(data);

            clearInterval(interval);
          } else if (data.status === "FAILED") {
            setState(data);
            clearInterval(interval);
            setError(ERROR_MESSAGES.SERVER_ERROR);
          }
        }
      })
    );
  };

  React.useEffect(() => {
    if (customerState.customerId) {
      fetchPoliciesByCustomerId(customerState.customerId as number);
    }
  }, [customerState.customerId]);

  React.useEffect(() => {
    let interval: any;
    if (code) {
      interval = setInterval(() => {
        fetchData(code, interval);
      }, 10000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [code]);

  const renderData = () => {
    if (!state || state.status === "IN_PROGRESS") {
      return (
        <>
          {/* <Breadcrumb
					title="Extract Products"
					items={BCrumb}
				/> */}
          <ProgressCard
            heading="Your document is being processed....!"
            subText="This may take a few moments. We're retrieving data from your document."
          />
        </>
      );
    }
    if (state.status === "FAILED") {
      return (
        <>
          {/* <Breadcrumb
					title="Extract Products"
					items={BCrumb}
				/> */}
          <ProgressCard
            isError={true}
            heading={"There was an error processing your document."}
            subText={"Please try again later or check the document format."}
          />
        </>
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      title="ARS Quote"
      actions={
        !code
          ? [
              {
                type: "button",
                variant: "contained",
                label: "Close",
                onClick: onClose,
              },
              {
                type: "button",
                variant: "contained",
                label: "Save",
                onClick: handleClick,
              },
            ]
          : []
      }
    >
      {!code && (
        <>
          {error && (
            <CustomAlert
              sx={{ mt: 2, mb: 1 }}
              severity="error"
              onClose={() => setError(null)}
            >
              {error}
            </CustomAlert>
          )}
          <Grid container spacing={1}>
            {showCustomerPolicy && (
              <>
                <Grid item xs={12}>
                  <CustomerAutoSearch
                    label="Customer"
                    value={customerState.customerId}
                    onSelect={handleCustomer}
                    //error={errors.customer_id}
                    //helperText={errors.customer_id}
                    //disabled={customer_id ? true : false}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomFormLabel>Policy</CustomFormLabel>
                  <ControlledCustomSelect
                    variant="outlined"
                    size="small"
                    type="string"
                    fullWidth
                    displayEmpty
                    name="customer_policy_id"
                    value={Number(customerState.customerPolicyId)}
                    onChange={handlePolicyChange}
                    placeholder="Select One"
                    //disabled={customer_policy_id ? true : false}
                    options={policiesList.map((item) => {
                      return {
                        label: item.policy_number,
                        value: item.customer_policy_id,
                      };
                    })}
                    // helperText={errors.customer_policy_id}
                  />
                </Grid>
              </>
            )}
            {["automobile", "Automobile Policy"].includes(
              customerState.type
            ) && (
              <Grid item xs={12}>
                <CustomFormLabel>Application </CustomFormLabel>
                <ControlledCustomSelect
                  name="subType"
                  fullWidth
                  value={subType || ""}
                  variant="outlined"
                  placeholder="Select One"
                  displayEmpty
                  options={[
                    { label: "Ontario", value: "ONTARIO" },
                    { label: "Alberta", value: "ALBERTA" },
                  ]}
                  onChange={(event) => {
                    setSubType(event.target.value as string);
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography>Upload File</Typography>
              <FileUpload
                accept="pdf"
                value={null}
                onChange={(newFile) => {
                  setFile(newFile);
                }}
                onDelete={() => {
                  setFile(null);
                }}
              />
            </Grid>
          </Grid>
        </>
      )}
      {code && renderData()}
    </Dialog>
  );
};
